import * as Popover from '@radix-ui/react-popover';
import { CalendarDays } from 'lucide-react';
import { type InputHTMLAttributes, type Ref, useEffect, useId, useRef, useState } from 'react';
import { DayPicker, type DropdownProps } from 'react-day-picker';

import { dateFieldError, dateValue, parseDateValue } from '../../lib/date-field';
import { Button, IconButton } from './index';
import { Select } from './Select';
import 'react-day-picker/style.css';

function CalendarDropdown({
  options,
  className: _className,
  style: _style,
  ...props
}: DropdownProps) {
  return (
    <Select {...props} className="ui-calendar-caption-select">
      {options?.map(option => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </Select>
  );
}

export function DateField({
  type,
  className = '',
  variant = 'default',
  value,
  defaultValue,
  onInput,
  onChange,
  onBlur,
  ref: forwarded,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  ref?: Ref<HTMLInputElement>;
  variant?: 'default' | 'inline' | 'cell';
}) {
  const input = useRef<HTMLInputElement>(null),
    id = useId();

  const [open, setOpen] = useState(false),
    [local, setLocal] = useState(String(value ?? defaultValue ?? '')),
    [label, setLabel] = useState(props['aria-label'] ?? 'date');

  const text = local,
    timed = type === 'datetime-local',
    selected = parseDateValue(text);

  const [month, setMonth] = useState(selected ?? new Date());

  useEffect(() => {
    if (value !== undefined) {
      setLocal(String(value));
    }
  }, [value]);

  useEffect(() => {
    input.current?.setCustomValidity(dateFieldError(text, timed, props.min, props.max));
  }, [text, timed, props.min, props.max]);

  useEffect(() => {
    if (!props['aria-label']) {
      setLabel(input.current?.labels?.[0]?.textContent?.trim() || 'date');
    }
  }, [props['aria-label']]);

  const emit = (next: string) => {
    const field = input.current;

    if (!field) {
      return;
    }

    // Dispatch a real input event so existing onInput/onChange, form validation and autosave keep their native contract.
    Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')!.set!.call(field, next);
    field.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const choose = (day: Date) => {
    emit(`${dateValue(day)}${timed ? `T${text.split('T')[1] || '09:00'}` : ''}`);

    if (!timed) {
      setOpen(false);
      input.current?.focus();
    }
  };

  const time = text.split('T')[1] || '09:00';

  return (
    <span className={`ui-date-field ${variant === 'cell' ? 'ui-date-field--cell' : ''}`}>
      <input
        {...props}
        aria-label={props['aria-label'] ?? label}
        id={props.id ?? id}
        ref={node => {
          input.current = node;

          if (typeof forwarded === 'function') {
            forwarded(node);
          } else if (forwarded) {
            forwarded.current = node;
          }
        }}
        type="text"
        className={`ui-input ${variant === 'inline' ? 'ui-input--inline' : variant === 'cell' ? 'ui-input--cell' : ''} ${className}`}
        value={text}
        placeholder={props.placeholder ?? (timed ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD')}
        onInput={event => {
          const next = event.currentTarget.value;

          event.currentTarget.setCustomValidity(dateFieldError(next, timed, props.min, props.max));
          setLocal(next);

          if (!dateFieldError(next, timed, props.min, props.max)) {
            onInput?.(event);
          }
        }}
        onChange={event => {
          if (!dateFieldError(event.currentTarget.value, timed, props.min, props.max)) {
            onChange?.(event);
          }
        }}
        onBlur={event => {
          if (!dateFieldError(event.currentTarget.value, timed, props.min, props.max)) {
            onBlur?.(event);
          }
        }}
      />
      <Popover.Root
        modal
        open={open}
        onOpenChange={next => {
          setOpen(next);

          if (next) {
            setMonth(selected ?? new Date());
          }
        }}
      >
        <Popover.Trigger asChild>
          <IconButton
            size={variant === 'cell' ? 'compact' : 'default'}
            label={`Choose ${label}`}
            className="ui-date-trigger"
            disabled={props.disabled || props.readOnly}
          >
            <CalendarDays aria-hidden="true" />
          </IconButton>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="ui-calendar-popover"
            align="start"
            sideOffset={6}
            collisionPadding={12}
            aria-label={`Choose ${label}`}
            onOpenAutoFocus={e => e.preventDefault()}
          >
            <DayPicker
              mode="single"
              required
              autoFocus
              selected={selected}
              month={month}
              onMonthChange={setMonth}
              onSelect={day => {
                if (day) {
                  choose(day);
                }
              }}
              showOutsideDays
              captionLayout="dropdown"
              startMonth={new Date(1900, 0)}
              endMonth={new Date(2100, 11)}
              components={{ Dropdown: CalendarDropdown }}
              disabled={[
                ...(props.min && parseDateValue(String(props.min))
                  ? [{ before: parseDateValue(String(props.min))! }]
                  : []),
                ...(props.max && parseDateValue(String(props.max))
                  ? [{ after: parseDateValue(String(props.max))! }]
                  : []),
              ]}
            />
            {timed && (
              <div className="ui-calendar-time">
                <span>Time</span>
                <Select
                  aria-label="Hour"
                  value={time.slice(0, 2)}
                  onChange={e =>
                    emit(
                      `${dateValue(selected ?? new Date())}T${e.target.value}:${time.slice(3, 5)}`,
                    )
                  }
                >
                  {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(hour => (
                    <option key={hour}>{hour}</option>
                  ))}
                </Select>
                <span>:</span>
                <Select
                  aria-label="Minute"
                  value={time.slice(3, 5)}
                  onChange={e =>
                    emit(
                      `${dateValue(selected ?? new Date())}T${time.slice(0, 2)}:${e.target.value}`,
                    )
                  }
                >
                  {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(minute => (
                    <option key={minute}>{minute}</option>
                  ))}
                </Select>
              </div>
            )}
            <div className="ui-calendar-footer">
              <Button size="compact" onClick={() => choose(new Date())}>
                Today
              </Button>
              {!props.required && (
                <Button
                  size="compact"
                  onClick={() => {
                    emit('');
                    setOpen(false);
                  }}
                >
                  Clear
                </Button>
              )}
              <Button
                size="compact"
                variant="primary"
                onClick={() => {
                  setOpen(false);
                  input.current?.focus();
                }}
              >
                Done
              </Button>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </span>
  );
}
